import { createRoot, decodeMutationBatch, } from "@dopejs/pingo-reconciler";
import { SemanticTreeMirror } from "@dopejs/pingo-a11y";
import { decodeInputBatch, encodeInputBatch, EVENT_FLAG_PRECISE_WHEEL, KEY_FLAG_REPEAT, NativeTextInputBridge, } from "@dopejs/pingo-editing";
import { KEYBOARD_CODES_BY_NAME, KEYBOARD_KEY_NAMES_BY_NAME, MAX_INPUT_BYTES, MAX_MUTATION_BYTES, } from "./generated";
/** Reported when the platform gives a key neither a known name nor one code point. */
const UNIDENTIFIED_KEY_NAME = KEYBOARD_KEY_NAMES_BY_NAME.get("Unidentified") ?? 0;
import { CanvasFrameSink, createDefaultRasterCache, } from "./main-thread";
import { detectHostCapabilities, selectHostTransport, } from "./capabilities";
import { PostMessageMutationTransport } from "./post-message";
import { SabMutationRing } from "./sab-ring";
import { SabMutationTransport } from "./sab-transport";
import { MutationSceneSnapshot } from "./scene-snapshot";
import { MutationTransportBackpressureError } from "./transport-errors";
import { createWasmCore } from "./wasm";
import { createRenderWorker, RenderWorkerClient, } from "./worker-client";
import { MediaPipeline } from "./media";
/**
 * Display cadence sampling for the Worker's render clock.
 *
 * A worker cannot read the refresh rate, and its clock otherwise assumes 60Hz,
 * which caps rendering there on a faster display. Four samples is enough for a
 * median to reject a startup hitch; the bounds span 240Hz to 30Hz, and a
 * measurement outside them -- a throttled tab, a sampler racing a resize -- is
 * discarded in favour of the Worker's own default.
 */
const FRAME_INTERVAL_SAMPLES = 4;
const MINIMUM_FRAME_MS = 4;
const MAXIMUM_FRAME_MS = 34;
/**
 * How long a double click waits for its editor to become active.
 *
 * Long enough for a Worker round trip on a loaded main thread, short enough
 * that a gesture the user has moved on from is dropped instead of applied.
 */
const PENDING_WORD_SELECTION_MS = 600;
/** Creates the M2 capability-driven Worker root with a production M1 fallback. */
export async function createHostedCanvasRoot(canvas, options = {}) {
    const controller = new HostedCanvasRootController(canvas, options);
    await controller.initialize();
    return controller;
}
class HostedCanvasRootController {
    #options;
    #recoverableSink = new RecoverableMutationSink();
    #anchorHandle;
    #frameIntervals = [];
    #frameIntervalHandle;
    #canvas;
    #client;
    #closePromise;
    #closing = false;
    #core;
    #decision;
    #frameSink;
    #inputSequence = 1;
    #eventSequence = 1;
    #eventTimestamps = new Map();
    #keyTimestamp;
    #wheelGesture;
    #pendingWheel;
    #wheelFrame;
    /** Newest requested window per virtual list, awaiting a single render. */
    #pendingRefills = new Map();
    #refillFrame;
    #resizeObserver;
    #observedSize;
    #inputDirectFrames = 0;
    #inputRing;
    #inputSabFallbackFrames = 0;
    #inputBridge;
    #lastInputRingMetrics;
    #mode = "main-thread";
    #lastTransportMetrics;
    #media;
    #nonPassiveRegions = [];
    #editingGeometry;
    /** Latest observed geometry, keyed by generation-bearing node id. */
    #layoutGeometry = new Map();
    /** Frame of the geometry currently held, for the monotonic gate. */
    #layoutGeometrySeq;
    #paintedText;
    #staleLayoutGeometryFrames = 0;
    /** A double click held until its editor becomes active; see the handler. */
    #pendingWordSelection;
    #textDragPointer;
    #semanticMirror;
    #mainFrameTimestamp;
    #recovery;
    #recovering = false;
    #reducedMotionQuery;
    #root;
    #transferred = false;
    #transport;
    #unmounted = false;
    #eventListenersAttached = false;
    constructor(canvas, options) {
        if (!(canvas instanceof HTMLCanvasElement))
            throw new TypeError("canvas must be HTMLCanvasElement");
        this.#canvas = canvas;
        this.#options = options;
        this.#inputBridge = this.createInputBridge(canvas);
        if (options.accessibility !== false && typeof canvas.insertAdjacentElement === "function") {
            this.#semanticMirror = new SemanticTreeMirror(canvas, {
                onActivateRequest: (nodeId) => {
                    try {
                        this.#root?.activateNode(nodeId);
                    }
                    catch (cause) {
                        this.#options.onHostError?.(toError(cause, "semantic activation failed"));
                    }
                },
                onFocusRequest: (nodeId) => {
                    try {
                        if (this.#root?.editableState(nodeId) !== undefined) {
                            this.focusEditableWithOrigin(nodeId, "accessibility");
                        }
                        else {
                            const eventId = this.#eventSequence;
                            this.#eventSequence = nextSequence(eventId);
                            this.sendInputCommands([
                                { type: "focusNode", eventId, nodeId, origin: "accessibility" },
                            ]);
                        }
                    }
                    catch (cause) {
                        this.#options.onHostError?.(toError(cause, "semantic focus request failed"));
                    }
                },
            });
        }
        const capabilities = options.capabilities ?? detectHostCapabilities(canvas, options.capabilityEnvironment);
        this.#decision = selectHostTransport(capabilities, options.transport);
    }
    get canvas() {
        return this.#canvas;
    }
    get decision() {
        return this.#decision;
    }
    get failed() {
        return this.requireRoot().failed;
    }
    styleMetrics() {
        return this.requireRoot().styleMetrics();
    }
    get mode() {
        return this.#mode;
    }
    /** Current Worker queue state, or the final snapshot retained after runtime fallback. */
    transportMetrics() {
        return this.#transport?.metrics() ?? this.#lastTransportMetrics;
    }
    mediaMetrics() {
        return this.#media?.metrics();
    }
    inputTransportMetrics() {
        const ring = this.#inputRing?.metrics() ?? this.#lastInputRingMetrics;
        return {
            directFrames: this.#inputDirectFrames,
            mode: this.#mode,
            ...(ring === undefined ? {} : { ring }),
            sabFallbackFrames: this.#inputSabFallbackFrames,
        };
    }
    /** Routes one versioned Input Stream transaction to the current Core owner. */
    /**
     * Sends input commands, stamped with the host's own sequence.
     *
     * The Core refuses a batch whose sequence is not newer than the last one it
     * accepted, and a refusal fails the frame. The host writes to that stream
     * too -- focus, scrolling and events all go through it -- so an application
     * that numbered its own batches would interleave with those and be rejected.
     * Callers pass commands; the sequence is not theirs to choose.
     */
    sendInput(commands) {
        if (commands.length === 0)
            return;
        this.dispatchInput(encodeInputBatch({ frameSeq: this.#inputSequence, commands }));
    }
    dispatchInput(bytes) {
        if (!(bytes instanceof Uint8Array))
            throw new TypeError("input must be Uint8Array");
        if (bytes.byteLength > MAX_INPUT_BYTES)
            throw new RangeError("input exceeds protocol limit");
        const { frameSeq } = decodeInputBatch(bytes);
        if (this.#closing || this.#unmounted)
            throw new Error("hosted root is closed");
        if (this.#recovering)
            throw new Error("hosted root is recovering");
        if (this.#mode === "main-thread") {
            const sink = this.#frameSink;
            if (sink === undefined)
                throw new Error("main-thread Core is not initialized");
            // Advance first: reverse-stream handlers may reentrantly send new input.
            this.#inputSequence = nextSequence(frameSeq);
            sink.input(bytes);
            this.#inputDirectFrames += 1;
            return;
        }
        const client = this.#client;
        if (client === undefined)
            throw new Error("render Worker is not initialized");
        const inputRing = this.#inputRing;
        if (this.#mode === "sab" && inputRing !== undefined) {
            if (bytes.byteLength <= inputRing.payloadBytes && inputRing.tryPublish(frameSeq, bytes)) {
                client.postInputWake();
            }
            else {
                // Preserve sequence order: the wake is posted before the copied
                // fallback, so the Worker drains older shared slots first.
                client.postInputWake();
                client.postInput(bytes);
                this.#inputDirectFrames += 1;
                this.#inputSabFallbackFrames += 1;
            }
        }
        else {
            client.postInput(bytes);
            this.#inputDirectFrames += 1;
        }
        this.#inputSequence = nextSequence(frameSeq);
    }
    /** Starts direct manipulation and cancels an existing fling. */
    beginScroll(target) {
        this.sendScroll([{ type: "scrollBegin", nodeId: scrollNodeId(target) }]);
    }
    /** Applies one timed logical content-offset sample. */
    scrollBy(target, deltaX, deltaY, elapsedMs) {
        if (!Number.isFinite(elapsedMs) || elapsedMs <= 0 || elapsedMs > 1000) {
            throw new RangeError("elapsedMs must be greater than zero and at most 1000");
        }
        const elapsedMicros = Math.max(1, Math.min(1_000_000, Math.round(elapsedMs * 1000)));
        this.sendScroll([
            {
                type: "scrollDelta",
                nodeId: scrollNodeId(target),
                deltaX,
                deltaY,
                elapsedMicros,
            },
        ]);
    }
    /** Ends direct manipulation and starts the Core-estimated fling. */
    endScroll(target) {
        this.sendScroll([{ type: "scrollEnd", nodeId: scrollNodeId(target) }]);
    }
    /** Cancels direct manipulation and retains only edge rebound. */
    cancelScroll(target) {
        this.sendScroll([{ type: "scrollCancel", nodeId: scrollNodeId(target) }]);
    }
    /** Sets Core-owned constant scroll velocity; two zero components stop it. */
    setScrollVelocity(target, velocityX, velocityY) {
        this.sendScroll([
            {
                type: "setScrollVelocity",
                nodeId: scrollNodeId(target),
                velocityX,
                velocityY,
            },
        ]);
    }
    /** Applies a deterministic reduced-motion override to the active Core owner. */
    setReducedMotion(reduced) {
        if (typeof reduced !== "boolean")
            throw new TypeError("reduced motion must be boolean");
        if (this.#closing || this.#unmounted)
            throw new Error("hosted root is closed");
        if (this.#recovering)
            throw new Error("hosted root is recovering");
        if (this.#mode === "main-thread") {
            const sink = this.#frameSink;
            if (sink === undefined)
                throw new Error("main-thread Core is not initialized");
            sink.setReducedMotion(reduced);
            return;
        }
        const client = this.#client;
        if (client === undefined)
            throw new Error("render Worker is not initialized");
        client.postReducedMotion(reduced);
    }
    /** Activates native text services for one mounted EditableText node. */
    focusEditable(target) {
        this.focusEditableWithOrigin(target, "programmatic");
    }
    focusEditableWithOrigin(target, origin) {
        const nodeId = scrollNodeId(target);
        const state = this.requireCoreRoot().editableState(nodeId);
        if (state === undefined)
            throw new Error(`node ${String(nodeId)} is not an editable target`);
        const eventId = this.#eventSequence;
        this.#eventSequence = nextSequence(eventId);
        this.sendInputCommands([{ type: "focusNode", eventId, nodeId, origin }]);
        this.sendInputCommands([{ type: "focusEditable", nodeId }]);
        this.#inputBridge.activate(state);
    }
    /**
     * Activates native text services over a document, for one focused block.
     *
     * The OS surface needs a value and a caret, and a document has no single
     * value. It gets the block the caret is in: that is the context an input
     * method needs to place its candidate window and to know what surrounds the
     * composition, and the Core is what tells the Shell which block that is.
     * Commands come back addressed to the document root, where the Core resolves
     * them against its own caret rather than against these offsets.
     */
    focusDocument(target, block) {
        const nodeId = scrollNodeId(target);
        const eventId = this.#eventSequence;
        this.#eventSequence = nextSequence(eventId);
        this.sendInputCommands([{ type: "focusNode", eventId, nodeId, origin: "programmatic" }]);
        this.sendInputCommands([{ type: "focusEditable", nodeId }]);
        this.#inputBridge.activate({
            inputMode: "text",
            multiline: true,
            nodeId,
            password: false,
            readOnly: false,
            revision: block.revision,
            selection: { anchor: block.anchor, focus: block.focus },
            value: block.text,
        });
    }
    /** Ends the active native editing surface without creating per-widget DOM. */
    blurEditable() {
        const nodeId = this.#inputBridge.activeNodeId;
        if (nodeId !== undefined) {
            const eventId = this.#eventSequence;
            this.#eventSequence = nextSequence(eventId);
            this.sendInputCommands([{ type: "blurNode", eventId, nodeId }]);
            this.sendInputCommands([{ type: "blurEditable", nodeId }]);
            // A document that is no longer the surface's draws neither caret nor
            // selection, so whatever the Shell floats over that selection has to go
            // too. Nothing happens for a node that is not a document.
            try {
                this.#root?.applyDocumentBlur(nodeId);
            }
            catch (cause) {
                this.#options.onHostError?.(toError(cause, "Shell document blur handler failed"));
            }
        }
        this.#inputBridge.deactivate();
    }
    /** Supplies Core-derived editor and caret bounds to the OS input service. */
    updateEditingGeometry(geometry) {
        this.#inputBridge.updateGeometry(geometry);
    }
    async initialize() {
        if (this.#root !== undefined)
            throw new Error("hosted root is already initialized");
        if (this.#decision.mode === "main-thread") {
            await this.initializeMainThread(this.#canvas);
            this.attachReducedMotionListener();
            this.attachCanvasEventListeners();
            return;
        }
        try {
            await this.initializeWorker(this.#decision.mode);
        }
        catch (cause) {
            const error = toError(cause, "render Worker initialization failed");
            this.#options.onHostError?.(error);
            this.disposeWorkerRuntime(error);
            if (this.#transferred) {
                this.#canvas = replaceTransferredCanvas(this.#canvas, this.#options);
                this.replaceInputBridge(this.#canvas);
            }
            this.#decision = runtimeFallbackDecision(this.#decision, error);
            await this.initializeMainThread(this.#canvas);
        }
        this.attachReducedMotionListener();
        this.attachCanvasEventListeners();
    }
    render(node) {
        this.requireRoot().render(node);
    }
    flushSync() {
        this.requireRoot().flushSync();
    }
    invokeCallback(callbackId) {
        this.requireRoot().invokeCallback(callbackId);
    }
    unmount() {
        if (this.#unmounted)
            return;
        this.requireRoot().unmount();
        this.#unmounted = true;
        void this.close().catch((cause) => {
            this.#options.onHostError?.(toError(cause, "hosted root close failed"));
        });
    }
    close() {
        this.#closePromise ??= this.closeOnce();
        return this.#closePromise;
    }
    async initializeWorker(mode) {
        const workerFactory = this.#options.workerFactory ?? createRenderWorker;
        const clientOptions = {
            ...(this.#options.initializationTimeoutMs === undefined
                ? {}
                : { initializationTimeoutMs: this.#options.initializationTimeoutMs }),
            ...(this.#options.onClockMetrics === undefined
                ? {}
                : { onClockMetrics: this.#options.onClockMetrics }),
            onFatal: (error) => this.handleWorkerFatal(error),
            ...(this.#options.onFrame === undefined
                ? {}
                : { onFrame: (report) => this.handleFrameReport(report) }),
            onVirtualRefills: (requests) => this.deferVirtualRefills(requests),
            onEditTransaction: (transaction) => this.handleEditTransaction(transaction),
            onStructureRequest: (request) => this.handleStructureRequest(request),
            onDocumentSelection: (report) => this.handleDocumentSelection(report),
            onEventTransaction: (transaction) => this.handleEventTransaction(transaction),
            onNonPassiveRegions: (regions) => this.handleNonPassiveRegions(regions),
            onEditingGeometry: (frame) => this.handleEditingGeometry(frame),
            onSemantics: (nodes) => this.handleSemantics(nodes),
            onLayoutGeometry: (frame) => this.handleLayoutGeometry(frame),
            onPaintedText: (snapshot) => this.handlePaintedText(snapshot),
            sessionId: nextSessionId(),
        };
        const client = new RenderWorkerClient(workerFactory(), clientOptions);
        this.#client = client;
        this.startFrameIntervalSampling();
        const workerCapabilities = await client.prepare();
        if (!workerCapabilities.offscreenCanvas) {
            throw new Error("render Worker reports OffscreenCanvas unavailable");
        }
        let selectedMode = mode;
        if (mode === "sab" && !workerCapabilities.sharedArrayBuffer) {
            if (this.#options.transport?.strict === true) {
                throw new Error("render Worker reports SharedArrayBuffer unavailable");
            }
            selectedMode = "post-message";
            this.#decision = {
                ...this.#decision,
                mode: selectedMode,
                reasons: [
                    ...this.#decision.reasons,
                    "Worker SAB unavailable",
                    "falling back to post-message",
                ],
            };
        }
        const offscreen = transferOrExplain(this.#canvas);
        this.#transferred = true;
        let transport;
        let ringBuffer;
        let inputRingBuffer;
        if (selectedMode === "sab") {
            const allocation = SabMutationRing.create(2, MAX_MUTATION_BYTES);
            ringBuffer = allocation.buffer;
            transport = new SabMutationTransport(client.endpoint, allocation.ring, {
                ...(this.#options.mutationAcknowledgementTimeoutMs === undefined
                    ? {}
                    : { acknowledgementTimeoutMs: this.#options.mutationAcknowledgementTimeoutMs }),
                onError: (error) => this.handleWorkerFatal(error),
                ...(this.#options.mutationBufferBytes === undefined
                    ? {}
                    : { maxBufferedBytes: this.#options.mutationBufferBytes }),
                sessionId: client.sessionId,
            });
            const inputAllocation = SabMutationRing.create(INPUT_RING_CAPACITY, INPUT_RING_PAYLOAD_BYTES);
            this.#inputRing = inputAllocation.ring;
            inputRingBuffer = inputAllocation.buffer;
        }
        else {
            transport = new PostMessageMutationTransport(client.endpoint, {
                ...(this.#options.mutationAcknowledgementTimeoutMs === undefined
                    ? {}
                    : { acknowledgementTimeoutMs: this.#options.mutationAcknowledgementTimeoutMs }),
                onError: (error) => this.handleWorkerFatal(error),
                ...(this.#options.mutationBufferBytes === undefined
                    ? {}
                    : { maxBufferedBytes: this.#options.mutationBufferBytes }),
                sessionId: client.sessionId,
            });
        }
        this.#transport = transport;
        await client.activate({
            canvas: offscreen,
            devicePixelRatio: devicePixelRatioOf(this.#canvas),
            height: positiveDimension(this.logicalHeight(), "canvas height"),
            incrementalPicturesEnabled: this.#options.incrementalPicturesEnabled ?? true,
            mode: selectedMode,
            rasterCache: this.#options.rasterCache === true,
            reducedMotion: reducedMotionPreference(this.#options.reducedMotion),
            ...(inputRingBuffer === undefined ? {} : { inputRingBuffer }),
            ...(ringBuffer === undefined ? {} : { ringBuffer }),
            ...(() => {
                const targetFrameMs = this.measuredFrameIntervalMs();
                return targetFrameMs === undefined ? {} : { targetFrameMs };
            })(),
            width: positiveDimension(this.logicalWidth(), "canvas width"),
        });
        if (client.state !== "ready")
            throw new Error("render Worker failed during activation");
        this.#recoverableSink.install(new TransportMutationSink(transport, (error) => this.handleWorkerFatal(error)));
        this.#root = createRoot(this.#recoverableSink, this.reconcilerOptions());
        this.#mode = selectedMode;
        this.startClockAnchors(client);
        this.setPaintedTextActive(this.#options.onPaintedText !== undefined);
        this.#options.onModeChange?.(this.#mode, this.#decision);
    }
    sendScroll(commands) {
        this.sendInputCommands(commands);
    }
    sendInputCommands(commands) {
        this.sendInput(commands);
    }
    reconcilerOptions() {
        return {
            ...this.#options,
            onLayoutObservationChange: (active) => this.setLayoutGeometryActive(active),
            onInteractionRequest: (request) => {
                this.handleInteractionRequest(request);
                this.#options.onInteractionRequest?.(request);
            },
            onMediaBinding: (binding, nodeId) => {
                this.mediaPipeline().bind(binding, nodeId);
                this.#options.onMediaBinding?.(binding, nodeId);
            },
        };
    }
    /**
     * Starts and stops the per-frame geometry export with the observed set.
     *
     * Worker mode has no direct handle on the sink, so the toggle rides the
     * protocol the same way every other worker-side setting does.
     */
    setLayoutGeometryActive(active) {
        this.#frameSink?.setLayoutGeometryActive(active);
        this.#client?.postLayoutGeometryActive(active);
    }
    /**
     * Starts and stops the painted-text render oracle.
     *
     * Called once after a transport comes up, because asking for the callback is
     * the ask; the toggle rides the worker protocol like every other setting.
     */
    setPaintedTextActive(active) {
        this.#frameSink?.setPaintedTextActive(active);
        this.#client?.postPaintedTextActive(active);
    }
    handlePaintedText(snapshot) {
        this.#paintedText = snapshot;
        this.#options.onPaintedText?.(snapshot);
    }
    handleInteractionRequest(request) {
        const eventId = this.#eventSequence;
        this.#eventSequence = nextSequence(eventId);
        switch (request.type) {
            case "setPointerCapture":
                if (typeof this.#canvas.setPointerCapture === "function") {
                    this.#canvas.setPointerCapture(request.pointerId);
                }
                this.sendInputCommands([
                    {
                        type: "setPointerCapture",
                        eventId,
                        pointerId: request.pointerId,
                        nodeId: request.nodeId,
                    },
                ]);
                return;
            case "releasePointerCapture":
                if (typeof this.#canvas.hasPointerCapture === "function" &&
                    this.#canvas.hasPointerCapture(request.pointerId) &&
                    typeof this.#canvas.releasePointerCapture === "function") {
                    this.#canvas.releasePointerCapture(request.pointerId);
                }
                this.sendInputCommands([
                    {
                        type: "releasePointerCapture",
                        eventId,
                        pointerId: request.pointerId,
                        nodeId: request.nodeId,
                    },
                ]);
                return;
            case "focus":
                // Focusing an editable has to start the editing session, not only move
                // interaction focus: Core activates native text services from
                // FocusEditable, so `ref.focus()` on an Input otherwise left the field
                // marked focused with no caret, no keyboard and no IME. This is the one
                // way a Shell can hand focus to a control from a press that landed on
                // its decorated wrapper rather than on the editable itself.
                if (this.#root?.editableState(request.nodeId) !== undefined) {
                    this.focusEditableWithOrigin(request.nodeId, "programmatic");
                    return;
                }
                this.sendInputCommands([
                    { type: "focusNode", eventId, nodeId: request.nodeId, origin: "programmatic" },
                ]);
                return;
            case "blur":
                // Symmetrically: ending interaction focus on the active editor has to
                // end its session, or the OS keeps typing into a field the Shell has
                // already blurred.
                if (this.#inputBridge.activeNodeId === request.nodeId) {
                    this.blurEditable();
                    return;
                }
                this.sendInputCommands([{ type: "blurNode", eventId, nodeId: request.nodeId }]);
                return;
            case "scrollTo":
                this.sendInputCommands([
                    { type: "scrollTo", nodeId: request.nodeId, x: request.x, y: request.y },
                ]);
                return;
            case "scrollBy":
                this.sendInputCommands([
                    {
                        type: "scrollBy",
                        nodeId: request.nodeId,
                        deltaX: request.deltaX,
                        deltaY: request.deltaY,
                    },
                ]);
                return;
            case "setScrollVelocity":
                this.sendInputCommands([
                    {
                        type: "setScrollVelocity",
                        nodeId: request.nodeId,
                        velocityX: request.velocityX,
                        velocityY: request.velocityY,
                    },
                ]);
                return;
            case "mediaPlay":
                this.mediaPipeline().play(request.nodeId);
                return;
            case "mediaPause":
                this.mediaPipeline().pause(request.nodeId);
                return;
            case "mediaSeek":
                this.mediaPipeline().seek(request.nodeId, request.timeSeconds);
                return;
        }
    }
    mediaPipeline() {
        this.#media ??= new MediaPipeline({
            transferableFrames: this.#mode !== "main-thread",
            target: {
                submit: (resourceId, source, path) => this.submitMediaFrame(resourceId, source, path),
            },
            createVideoFrame: createTransferableVideoFrame,
            onMetadata: (nodeId, width, height) => {
                try {
                    this.#root?.updateMediaMetadata(nodeId, width, height);
                }
                catch (cause) {
                    this.#options.onHostError?.(toError(cause, "media metadata update failed"));
                }
            },
            onEvent: (nodeId, event) => {
                try {
                    this.#root?.applyMediaEvent(nodeId, event);
                }
                catch (cause) {
                    this.#options.onHostError?.(toError(cause, "media event dispatch failed"));
                }
            },
            ...(this.#options.onMediaMetrics === undefined
                ? {}
                : { onMetrics: this.#options.onMediaMetrics }),
        });
        return this.#media;
    }
    submitMediaFrame(resourceId, source, path) {
        if (this.#mode === "main-thread") {
            const sink = this.#frameSink;
            if (sink === undefined)
                throw new Error("main-thread media sink is unavailable");
            sink.updateVideoFrame(resourceId, source, path);
            return;
        }
        if (path === "html-media")
            throw new Error("HTMLMediaElement cannot cross the Worker boundary");
        const client = this.#client;
        if (client === undefined)
            throw new Error("render Worker media sink is unavailable");
        client.postMediaFrame(resourceId, source, path);
    }
    handleCanvasPointerEvent = (event) => {
        // A new press supersedes a held gesture. The browser reports the double
        // click after both presses, so this never discards the one just recorded.
        if (event.type === "pointerdown")
            this.#pendingWordSelection = undefined;
        switch (event.type) {
            case "pointerdown":
            case "pointerup":
            case "pointermove":
            case "pointercancel":
            case "pointerleave":
                this.dispatchCanvasEvent(event.type, event, 0, 0);
        }
    };
    handleCanvasClick = (event) => {
        // A third click has no event of its own: the browser reports it as a click
        // whose count says three.
        if (event.detail >= 3) {
            const point = this.canvasPoint(event);
            if (point !== undefined)
                this.selectBlockAt(point[0], point[1]);
        }
        this.dispatchCanvasEvent("click", event, 0, 0);
    };
    handleCanvasContextMenu = (event) => {
        // The platform menu is suppressed unconditionally, not only when a handler
        // exists: whether one exists is Core's answer after a hit test, and by the
        // time that answer arrives the event is no longer cancellable.
        if (event.cancelable)
            event.preventDefault();
        this.dispatchCanvasEvent("contextmenu", event, 0, 0);
    };
    handleCanvasKeyDown = (event) => {
        this.dispatchCanvasKeyEvent("keydown", event);
    };
    handleCanvasKeyUp = (event) => {
        this.dispatchCanvasKeyEvent("keyup", event);
    };
    /**
     * Interns one `KeyboardEvent` and hands it to Core for focus routing.
     *
     * `key` and `code` are strings, and putting a string on a per-event binary
     * path would allocate every keystroke and break replay determinism, so both
     * are interned against the schema tables. A key the tables do not know still
     * reaches the Shell: `code` comes back empty, but a printable `key` survives
     * as its code point.
     *
     * Nothing here can produce text. Insertion comes from the editing
     * transaction path (EditContext or the textarea proxy); Core never derives an
     * edit from a key. During composition the key is reported as `Process`, which
     * is what a browser reports too.
     */
    dispatchCanvasKeyEvent(kind, event) {
        if (this.#closing || this.#unmounted || this.#recovering)
            return;
        const composing = event.isComposing === true || event.keyCode === 229;
        const name = composing ? "Process" : event.key;
        const keyName = KEYBOARD_KEY_NAMES_BY_NAME.get(name) ?? 0;
        // A single code point is carried as text; anything else has to be a name.
        const keyText = keyName === 0 && [...name].length === 1 ? (name.codePointAt(0) ?? 0) : 0;
        const timestamp = Number.isFinite(event.timeStamp) ? event.timeStamp : 0;
        const previous = this.#keyTimestamp;
        this.#keyTimestamp = timestamp;
        const elapsedMs = previous === undefined || timestamp <= previous
            ? 1000 / 60
            : Math.min(1000, timestamp - previous);
        const eventId = this.#eventSequence;
        this.#eventSequence = nextSequence(eventId);
        try {
            this.sendInputCommands([
                {
                    type: "dispatchKeyEvent",
                    eventId,
                    kind,
                    flags: event.repeat ? KEY_FLAG_REPEAT : 0,
                    keyCode: KEYBOARD_CODES_BY_NAME.get(event.code) ?? 0,
                    keyName: keyName === 0 && keyText === 0 ? UNIDENTIFIED_KEY_NAME : keyName,
                    keyText,
                    modifiers: (event.shiftKey ? 1 : 0) |
                        (event.ctrlKey ? 2 : 0) |
                        (event.altKey ? 4 : 0) |
                        (event.metaKey ? 8 : 0),
                    elapsedMicros: Math.max(1, Math.min(1_000_000, Math.round(elapsedMs * 1000))),
                },
            ]);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, `${kind} dispatch failed`));
        }
    }
    handleCanvasWheel = (event) => {
        const scale = event.deltaMode === 1
            ? 16
            : event.deltaMode === 2
                ? Math.max(1, this.#canvas.clientHeight)
                : 1;
        const flags = this.classifyWheel(event);
        // A pointing device emits one event per display refresh, and each one used
        // to become its own Core transaction: a frame painted and the whole canvas
        // replayed for a picture that the next event superseded before it could be
        // seen. Merging a frame's worth of deltas into one command keeps every
        // pixel of motion while paying for one replay instead of dozens.
        // Suppression has to stay synchronous: a deferred preventDefault arrives
        // after the browser has already scrolled the page, so the canvas and the
        // page move together.
        this.suppressWheelDefault(event);
        const pending = this.#pendingWheel;
        if (pending !== undefined && pending.flags === flags) {
            pending.deltaX += event.deltaX * scale;
            pending.deltaY += event.deltaY * scale;
            pending.event = event;
            return;
        }
        this.flushWheel();
        this.#pendingWheel = {
            deltaX: event.deltaX * scale,
            deltaY: event.deltaY * scale,
            event,
            flags,
        };
        if (typeof requestAnimationFrame === "function") {
            this.#wheelFrame = requestAnimationFrame(() => {
                this.#wheelFrame = undefined;
                this.flushWheel();
            });
        }
        else {
            this.flushWheel();
        }
    };
    /**
     * Cancels the browser's own scrolling when Core owns the wheel here.
     *
     * Runs on the event itself rather than on the coalesced flush, because
     * `preventDefault` is only honoured while the event is being dispatched.
     */
    suppressWheelDefault(event) {
        if (!event.cancelable || this.#nonPassiveRegions.length === 0)
            return;
        const rect = this.#canvas.getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0))
            return;
        const x = ((event.clientX - rect.left) * this.logicalWidth()) / rect.width;
        const y = ((event.clientY - rect.top) * this.logicalHeight()) / rect.height;
        const suppressed = this.#nonPassiveRegions.some((region) => (region.flags & 1) !== 0 &&
            x >= region.left &&
            x < region.right &&
            y >= region.top &&
            y < region.bottom);
        if (suppressed)
            event.preventDefault();
    }
    /** Sends the merged wheel delta collected during this frame, if any. */
    flushWheel() {
        const pending = this.#pendingWheel;
        if (pending === undefined)
            return;
        this.#pendingWheel = undefined;
        this.dispatchCanvasEvent("wheel", pending.event, pending.deltaX, pending.deltaY, pending.flags);
    }
    /**
     * Classifies a wheel sample as a high-precision gesture or a discrete notch.
     *
     * Core applies high-precision deltas one-to-one and animates discrete
     * notches, so a misclassification changes feel rather than distance. The
     * decision is per gesture, not per event: a classic wheel produces
     * multiple-of-120 legacy deltas spaced far apart, while a trackpad streams
     * samples at display rate. A gesture that shows either trackpad trait stays
     * high-precision until it ends, and an unknown platform stays
     * high-precision so the applied motion matches the raw delta.
     */
    classifyWheel(event) {
        const timestamp = Number.isFinite(event.timeStamp) ? event.timeStamp : 0;
        const previous = this.#wheelGesture;
        const continuing = previous !== undefined && timestamp - previous.timestamp <= WHEEL_GESTURE_GAP_MS;
        if (continuing && previous.precise) {
            this.#wheelGesture = { precise: true, timestamp };
            return EVENT_FLAG_PRECISE_WHEEL;
        }
        const legacy = event.wheelDeltaY;
        const notched = event.deltaMode !== 0 ||
            (typeof legacy === "number" && legacy !== 0 && legacy % WHEEL_NOTCH_LEGACY_DELTA === 0);
        const streaming = continuing && timestamp - previous.timestamp < WHEEL_STREAM_INTERVAL_MS;
        const precise = !notched || streaming;
        this.#wheelGesture = { precise, timestamp };
        return precise ? EVENT_FLAG_PRECISE_WHEEL : 0;
    }
    dispatchCanvasEvent(kind, event, deltaX, deltaY, flags = 0) {
        if (this.#closing || this.#unmounted || this.#recovering)
            return;
        const rect = this.#canvas.getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0))
            return;
        const x = ((event.clientX - rect.left) * this.logicalWidth()) / rect.width;
        const y = ((event.clientY - rect.top) * this.logicalHeight()) / rect.height;
        const pointerId = kind.startsWith("pointer") ? event.pointerId >>> 0 : 0;
        const pointer = kind.startsWith("pointer") ? event : undefined;
        const pointerType = pointer?.pointerType === "mouse" ||
            pointer?.pointerType === "pen" ||
            pointer?.pointerType === "touch"
            ? pointer.pointerType
            : pointerId === 0
                ? "none"
                : "mouse";
        // Wheel suppression runs on the listener instead, because this dispatch is
        // deferred to the coalescing frame and `preventDefault` is only honoured
        // while the event is being dispatched.
        const suppressionFlag = kind === "wheel" ? 0 : event.pointerType === "touch" ? 2 : 0;
        const suppressed = suppressionFlag !== 0 &&
            this.#nonPassiveRegions.some((region) => (region.flags & suppressionFlag) !== 0 &&
                x >= region.left &&
                x < region.right &&
                y >= region.top &&
                y < region.bottom);
        if (suppressed) {
            if (event.cancelable)
                event.preventDefault();
            if (kind === "pointerdown" &&
                pointerId !== 0 &&
                typeof this.#canvas.setPointerCapture === "function") {
                this.#canvas.setPointerCapture(pointerId);
            }
        }
        if ((kind === "pointerup" || kind === "pointercancel") &&
            pointerId !== 0 &&
            typeof this.#canvas.hasPointerCapture === "function" &&
            this.#canvas.hasPointerCapture(pointerId)) {
            this.#canvas.releasePointerCapture(pointerId);
        }
        const modifiers = (event.shiftKey ? 1 : 0) |
            (event.ctrlKey ? 2 : 0) |
            (event.altKey ? 4 : 0) |
            (event.metaKey ? 8 : 0);
        const timestampKey = kind === "wheel" ? -1 : pointerId;
        const timestamp = Number.isFinite(event.timeStamp) ? event.timeStamp : 0;
        const previousTimestamp = this.#eventTimestamps.get(timestampKey);
        if (kind === "pointerup" || kind === "pointercancel") {
            this.#eventTimestamps.delete(timestampKey);
        }
        else {
            this.#eventTimestamps.set(timestampKey, timestamp);
        }
        const elapsedMs = previousTimestamp === undefined || timestamp <= previousTimestamp
            ? 1000 / 60
            : Math.min(1000, timestamp - previousTimestamp);
        this.blurEditableOutsideActiveEditor(kind, x, y);
        this.synthesizeTextSelection(kind, x, y, pointerId, event.shiftKey);
        const eventId = this.#eventSequence;
        this.#eventSequence = nextSequence(eventId);
        try {
            this.sendInputCommands([
                {
                    type: "dispatchEvent",
                    eventId,
                    kind,
                    flags,
                    x,
                    y,
                    deltaX,
                    deltaY,
                    buttons: event.buttons & 0xffff,
                    modifiers,
                    pointerId,
                    elapsedMicros: Math.max(1, Math.min(1_000_000, Math.round(elapsedMs * 1000))),
                    pointerType,
                    isPrimary: pointer?.isPrimary === true,
                    pressure: Math.max(0, Math.min(1, pointer?.pressure ?? 0)),
                    tiltX: Math.max(-90, Math.min(90, pointer?.tiltX ?? 0)),
                    tiltY: Math.max(-90, Math.min(90, pointer?.tiltY ?? 0)),
                    width: Math.max(0, pointer?.width ?? 0),
                    height: Math.max(0, pointer?.height ?? 0),
                },
            ]);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, `${kind} dispatch failed`));
        }
    }
    /**
     * Ends the session when a press lands anywhere but the active editor.
     *
     * Core reports only a hit target, so a press on empty canvas produces no
     * event transaction at all and cannot drive this. The active editor's control
     * bounds already come back with the editing geometry, so the decision is made
     * here and synchronously, the way a native input loses focus.
     */
    blurEditableOutsideActiveEditor(kind, x, y) {
        if (kind !== "pointerdown")
            return;
        const activeNodeId = this.#inputBridge.activeNodeId;
        if (activeNodeId === undefined)
            return;
        const geometry = this.#editingGeometry;
        // Bounds unknown: blurring on a guess would end a session the press was
        // actually inside of, which is worse than leaving it focused for a frame.
        if (geometry === undefined || geometry.nodeId !== activeNodeId)
            return;
        const bounds = geometry.controlBounds;
        if (x >= bounds.left &&
            x < bounds.left + bounds.width &&
            y >= bounds.top &&
            y < bounds.top + bounds.height) {
            return;
        }
        try {
            this.blurEditable();
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "editable blur failed"));
        }
    }
    /**
     * Ends the session when a press lands outside the canvas entirely.
     *
     * The engine never sees those events, so without this the session outlives
     * every interaction with the rest of the page. The accessibility mirror and
     * the input proxy are the engine's own surfaces and do not count as outside.
     */
    handleDocumentPointerDown = (event) => {
        if (this.#closing || this.#unmounted)
            return;
        if (this.#inputBridge.activeNodeId === undefined)
            return;
        const target = event.target;
        if (!(target instanceof Node))
            return;
        if (this.#canvas === target || this.#canvas.contains(target))
            return;
        if (this.#inputBridge.ownsNode(target))
            return;
        if (this.#semanticMirror?.container.contains(target) === true)
            return;
        // Decided after the event has finished dispatching, not during it. This
        // listener captures, so that a handler which stops propagation cannot
        // strand the session -- but that also puts it ahead of the application,
        // whose own controls have not had their say yet. A microtask runs once
        // dispatch is over, so the session still ends for a press nobody claimed,
        // and a control that belongs to the editor keeps it by preventing the
        // press: a toolbar over the selection, a handle being dragged.
        queueMicrotask(() => {
            if (this.#closing || this.#unmounted || event.defaultPrevented)
                return;
            if (this.#inputBridge.activeNodeId === undefined)
                return;
            try {
                this.blurEditable();
            }
            catch (cause) {
                this.#options.onHostError?.(toError(cause, "editable blur failed"));
            }
        });
    };
    handleWindowBlur = () => {
        this.sendInteractionReset("windowBlur");
    };
    handleVisibilityChange = () => {
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
            this.sendInteractionReset("documentHidden");
        }
    };
    sendInteractionReset(reason) {
        if (this.#closing || this.#unmounted || this.#recovering)
            return;
        this.#eventTimestamps.clear();
        this.#textDragPointer = undefined;
        const eventId = this.#eventSequence;
        this.#eventSequence = nextSequence(eventId);
        try {
            this.sendInputCommands([{ type: "resetInteraction", eventId, reason }]);
        }
        catch (cause) {
            this.#root?.resetInteractionState();
            this.#options.onHostError?.(toError(cause, "interaction reset failed"));
        }
    }
    /** Turns raw pointer input over the active editor into caret placement. */
    synthesizeTextSelection(kind, x, y, pointerId, shiftKey) {
        const activeNodeId = this.#inputBridge.activeNodeId;
        if (activeNodeId === undefined) {
            this.#textDragPointer = undefined;
            return;
        }
        const send = (extend, word) => {
            try {
                this.sendInputCommands([{ type: "placeCaret", nodeId: activeNodeId, x, y, extend, word }]);
            }
            catch (cause) {
                this.#options.onHostError?.(toError(cause, "caret placement failed"));
            }
        };
        switch (kind) {
            case "pointerdown": {
                const geometry = this.#editingGeometry;
                const inside = geometry !== undefined &&
                    geometry.nodeId === activeNodeId &&
                    x >= geometry.controlBounds.left &&
                    x < geometry.controlBounds.left + geometry.controlBounds.width &&
                    y >= geometry.controlBounds.top &&
                    y < geometry.controlBounds.top + geometry.controlBounds.height;
                if (!inside)
                    return;
                this.#textDragPointer = pointerId;
                // Captured, so the drag survives leaving the canvas. Selecting text and
                // pulling past the edge is how a reader selects more than a screenful,
                // and without the capture every move outside the element goes to
                // whatever is under the pointer instead.
                this.captureTextDrag(pointerId);
                send(shiftKey, false);
                return;
            }
            case "pointermove":
                if (this.#textDragPointer === pointerId)
                    send(true, false);
                return;
            case "pointerup":
            case "pointercancel":
            case "pointerleave":
                if (this.#textDragPointer === pointerId)
                    this.releaseTextDrag(pointerId);
                return;
            default:
        }
    }
    /** Routes a drag's later moves to the canvas even once it leaves. */
    captureTextDrag(pointerId) {
        const canvas = this.#canvas;
        try {
            canvas.setPointerCapture?.(pointerId);
        }
        catch {
            // A pointer the browser has already released cannot be captured, and a
            // drag that has ended needs no capture. Selection still works inside the
            // canvas either way.
        }
    }
    releaseTextDrag(pointerId) {
        this.#textDragPointer = undefined;
        const canvas = this.#canvas;
        try {
            if (canvas.hasPointerCapture?.(pointerId) === true) {
                canvas.releasePointerCapture?.(pointerId);
            }
        }
        catch {
            // Already released, which is the state this wanted.
        }
    }
    /** The canvas-space point a mouse event names, or nothing when unsized. */
    canvasPoint(event) {
        const rect = this.#canvas.getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0))
            return undefined;
        return [
            ((event.clientX - rect.left) * this.logicalWidth()) / rect.width,
            ((event.clientY - rect.top) * this.logicalHeight()) / rect.height,
        ];
    }
    /**
     * Selects the block under a third click.
     *
     * Composed from commands that already exist rather than given a flag of its
     * own: a press to land in the block, then its two edges. The Core resolves
     * each against the state the one before it left, so the three arrive as one
     * frame and the reader never sees the caret on its way.
     */
    selectBlockAt(x, y) {
        const activeNodeId = this.#inputBridge.activeNodeId;
        const geometry = this.#editingGeometry;
        if (activeNodeId === undefined || geometry === undefined || geometry.nodeId !== activeNodeId) {
            return;
        }
        const bounds = geometry.controlBounds;
        if (x < bounds.left ||
            x >= bounds.left + bounds.width ||
            y < bounds.top ||
            y >= bounds.top + bounds.height) {
            return;
        }
        try {
            this.sendInputCommands([
                { type: "placeCaret", nodeId: activeNodeId, x, y, extend: false, word: false },
                {
                    type: "moveCaret",
                    nodeId: activeNodeId,
                    direction: "lineStart",
                    granularity: "grapheme",
                    extend: false,
                },
                {
                    type: "moveCaret",
                    nodeId: activeNodeId,
                    direction: "lineEnd",
                    granularity: "grapheme",
                    extend: true,
                },
            ]);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "block selection failed"));
        }
    }
    handleCanvasDoubleClick = (event) => {
        const mouse = event;
        const point = this.canvasPoint(mouse);
        if (point === undefined)
            return;
        const [x, y] = point;
        if (this.selectWordAt(x, y))
            return;
        // The press that focuses an editor round-trips through Core, and with a
        // Worker transport that has not landed by the time the browser reports the
        // double click. Dropping it made the first double click on an unfocused
        // field place a caret and select nothing, which reads as word selection
        // being broken. Held until the editor reports its geometry.
        this.#pendingWordSelection = { x, y, deadline: this.now() + PENDING_WORD_SELECTION_MS };
    };
    /**
     * Selects the word under a canvas point when an editor already owns it.
     *
     * Returns whether the selection was dispatched, so the caller can decide to
     * hold the gesture until an editor becomes active.
     */
    selectWordAt(x, y) {
        const activeNodeId = this.#inputBridge.activeNodeId;
        const geometry = this.#editingGeometry;
        if (activeNodeId === undefined || geometry === undefined || geometry.nodeId !== activeNodeId) {
            return false;
        }
        const bounds = geometry.controlBounds;
        if (x < bounds.left ||
            x >= bounds.left + bounds.width ||
            y < bounds.top ||
            y >= bounds.top + bounds.height) {
            return false;
        }
        try {
            // The segmentation travels with the click: Core resolves the offset from
            // the caret stops, and this only decides where the word around it ends.
            const words = this.#inputBridge.wordBoundaries();
            this.sendInputCommands([
                ...(words === undefined
                    ? []
                    : [
                        {
                            type: "setWordBoundaries",
                            nodeId: activeNodeId,
                            baseRevision: words.baseRevision,
                            boundaries: words.offsets,
                        },
                    ]),
                { type: "placeCaret", nodeId: activeNodeId, x, y, extend: false, word: true },
            ]);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "word selection failed"));
        }
        return true;
    }
    /** Applies a double click that arrived before its editor was active. */
    flushPendingWordSelection() {
        const pending = this.#pendingWordSelection;
        if (pending === undefined)
            return;
        // Expired gestures are dropped rather than applied late: selecting a word
        // long after the press would move a selection the user has since made.
        if (this.now() > pending.deadline) {
            this.#pendingWordSelection = undefined;
            return;
        }
        // Cleared before dispatching, not after: the command produces a frame, the
        // frame reports geometry, and that re-enters here. Leaving it set resent the
        // selection on every re-entry until the deadline expired.
        this.#pendingWordSelection = undefined;
        if (!this.selectWordAt(pending.x, pending.y))
            this.#pendingWordSelection = pending;
    }
    now() {
        return typeof performance === "object" ? performance.now() : Date.now();
    }
    attachCanvasEventListeners() {
        this.observeCanvasSize();
        if (this.#eventListenersAttached)
            return;
        if (typeof this.#canvas.addEventListener !== "function")
            return;
        const pointerPassive = !this.#nonPassiveRegions.some((region) => (region.flags & 2) !== 0);
        const wheelPassive = !this.#nonPassiveRegions.some((region) => (region.flags & 1) !== 0);
        this.applyTouchAction(!pointerPassive);
        this.#canvas.addEventListener("pointerdown", this.handleCanvasPointerEvent, {
            passive: pointerPassive,
        });
        this.#canvas.addEventListener("pointerup", this.handleCanvasPointerEvent, {
            passive: pointerPassive,
        });
        this.#canvas.addEventListener("pointermove", this.handleCanvasPointerEvent, {
            passive: pointerPassive,
        });
        this.#canvas.addEventListener("pointercancel", this.handleCanvasPointerEvent, {
            passive: pointerPassive,
        });
        this.#canvas.addEventListener("pointerleave", this.handleCanvasPointerEvent, {
            passive: true,
        });
        this.#canvas.addEventListener("click", this.handleCanvasClick, { passive: true });
        this.#canvas.addEventListener("dblclick", this.handleCanvasDoubleClick, { passive: true });
        this.#canvas.addEventListener("wheel", this.handleCanvasWheel, { passive: wheelPassive });
        // Non-passive: an application has to be able to stop a key from also
        // scrolling or tabbing the page. A canvas only receives key events when it
        // is focusable, and making it so is the host's job, not the application's.
        if (typeof this.#canvas.getAttribute === "function" && this.#canvas.tabIndex < 0) {
            this.#canvas.tabIndex = 0;
        }
        this.#canvas.addEventListener("contextmenu", this.handleCanvasContextMenu, {
            passive: false,
        });
        this.#canvas.addEventListener("keydown", this.handleCanvasKeyDown, { passive: false });
        this.#canvas.addEventListener("keyup", this.handleCanvasKeyUp, { passive: false });
        if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
            // Capture, so a handler that stops propagation cannot strand the session.
            document.addEventListener("pointerdown", this.handleDocumentPointerDown, {
                capture: true,
                passive: true,
            });
            document.addEventListener("visibilitychange", this.handleVisibilityChange, {
                passive: true,
            });
        }
        if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
            window.addEventListener("blur", this.handleWindowBlur, { passive: true });
        }
        this.#eventListenersAttached = true;
    }
    detachCanvasEventListeners() {
        if (this.#wheelFrame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.#wheelFrame);
        }
        this.#wheelFrame = undefined;
        this.#pendingWheel = undefined;
        if (this.#refillFrame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.#refillFrame);
        }
        this.#refillFrame = undefined;
        this.#pendingRefills.clear();
        if (!this.#eventListenersAttached)
            return;
        if (typeof this.#canvas.removeEventListener !== "function")
            return;
        if (typeof document !== "undefined" && typeof document.removeEventListener === "function") {
            document.removeEventListener("pointerdown", this.handleDocumentPointerDown, {
                capture: true,
            });
            document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        }
        if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
            window.removeEventListener("blur", this.handleWindowBlur);
        }
        this.#canvas.removeEventListener("contextmenu", this.handleCanvasContextMenu);
        this.#canvas.removeEventListener("keydown", this.handleCanvasKeyDown);
        this.#canvas.removeEventListener("keyup", this.handleCanvasKeyUp);
        this.#canvas.removeEventListener("pointerdown", this.handleCanvasPointerEvent);
        this.#canvas.removeEventListener("pointerup", this.handleCanvasPointerEvent);
        this.#canvas.removeEventListener("pointermove", this.handleCanvasPointerEvent);
        this.#canvas.removeEventListener("pointercancel", this.handleCanvasPointerEvent);
        this.#canvas.removeEventListener("pointerleave", this.handleCanvasPointerEvent);
        this.#canvas.removeEventListener("click", this.handleCanvasClick);
        this.#canvas.removeEventListener("dblclick", this.handleCanvasDoubleClick);
        this.#canvas.removeEventListener("wheel", this.handleCanvasWheel);
        this.applyTouchAction(false);
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = undefined;
        this.#eventListenersAttached = false;
    }
    attachReducedMotionListener() {
        if (typeof this.#options.reducedMotion === "boolean" ||
            this.#reducedMotionQuery !== undefined) {
            return;
        }
        const matchMedia = globalThis.matchMedia;
        if (typeof matchMedia !== "function")
            return;
        const query = matchMedia("(prefers-reduced-motion: reduce)");
        this.#reducedMotionQuery = query;
        if (typeof query.addEventListener === "function") {
            query.addEventListener("change", this.handleReducedMotionChange);
            return;
        }
        const legacy = query;
        legacy.addListener?.(this.handleReducedMotionChange);
    }
    detachReducedMotionListener() {
        const query = this.#reducedMotionQuery;
        this.#reducedMotionQuery = undefined;
        if (query === undefined)
            return;
        if (typeof query.removeEventListener === "function") {
            query.removeEventListener("change", this.handleReducedMotionChange);
            return;
        }
        const legacy = query;
        legacy.removeListener?.(this.handleReducedMotionChange);
    }
    handleReducedMotionChange = (event) => {
        if (this.#closing || this.#recovering)
            return;
        try {
            this.setReducedMotion(event.matches);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "reduced-motion update failed"));
        }
    };
    async initializeMainThread(canvas) {
        const width = positiveDimension(this.logicalWidth(), "canvas width");
        const height = positiveDimension(this.logicalHeight(), "canvas height");
        const context = contextOrExplain(canvas);
        if (context === null)
            throw new Error("Canvas2D context is unavailable");
        const core = await (this.#options.coreFactory ?? createWasmCore)(width, height);
        this.#core = core;
        core.set_reduced_motion?.(reducedMotionPreference(this.#options.reducedMotion));
        const rasterCache = this.#options.rasterCache === true
            ? createDefaultRasterCache(context, this.#options.onHostError)
            : undefined;
        const sink = new CanvasFrameSink(context, core, this.#options.onFrame === undefined ? undefined : (report) => this.handleFrameReport(report), rasterCache, (requests) => this.deferVirtualRefills(requests), this.#options.onHostError, (transaction) => this.handleEditTransaction(transaction), (transaction) => this.handleEventTransaction(transaction), (regions) => this.handleNonPassiveRegions(regions), (frame) => this.handleEditingGeometry(frame), (nodes) => this.handleSemantics(nodes), this.#options.incrementalPicturesEnabled ?? true, (frame) => this.handleLayoutGeometry(frame), (snapshot) => this.handlePaintedText(snapshot), (request) => this.handleStructureRequest(request), (report) => this.handleDocumentSelection(report));
        this.#frameSink = sink;
        this.#recoverableSink.install(sink);
        this.#root ??= createRoot(this.#recoverableSink, this.reconcilerOptions());
        this.#mode = "main-thread";
        this.startMainThreadClock(sink);
        this.setPaintedTextActive(this.#options.onPaintedText !== undefined);
        this.#options.onModeChange?.(this.#mode, this.#decision);
    }
    /**
     * Routes a structure request to the document that was asked.
     *
     * Delivered to the mounted document first and to the root observer second,
     * the way an edit transaction is: a document is a component someone can
     * mount twice, so the root cannot be the only place that hears about it.
     */
    handleStructureRequest(request) {
        const errors = [];
        try {
            this.#root?.applyDocumentStructure(request);
        }
        catch (cause) {
            errors.push(toError(cause, "Shell structure request handler failed"));
        }
        try {
            this.#options.onStructureRequest?.(request);
        }
        catch (cause) {
            errors.push(toError(cause, "host structure request observer failed"));
        }
        for (const error of errors)
            this.#options.onHostError?.(error);
    }
    handleDocumentSelection(report) {
        const errors = [];
        try {
            this.#root?.applyDocumentSelection(report);
        }
        catch (cause) {
            errors.push(toError(cause, "Shell document selection handler failed"));
        }
        try {
            this.#options.onDocumentSelection?.(report);
        }
        catch (cause) {
            errors.push(toError(cause, "host document selection observer failed"));
        }
        for (const error of errors)
            this.#options.onHostError?.(error);
    }
    handleEditTransaction(transaction) {
        const errors = [];
        try {
            this.#root?.applyEditTransaction(transaction);
        }
        catch (cause) {
            errors.push(toError(cause, "Shell edit transaction handler failed"));
        }
        try {
            this.#inputBridge.applyTransaction(transaction);
        }
        catch (cause) {
            this.#inputBridge.deactivate();
            errors.push(toError(cause, "native edit transaction synchronization failed"));
        }
        try {
            this.#options.onEditTransaction?.(transaction);
        }
        catch (cause) {
            errors.push(toError(cause, "host edit transaction observer failed"));
        }
        for (const error of errors)
            this.#options.onHostError?.(error);
    }
    handleFrameReport(report) {
        this.#options.onFrame?.({
            ...report,
            ...(this.#root === undefined ? {} : { style: this.#root.styleMetrics() }),
        });
    }
    handleEventTransaction(transaction) {
        if (cursorEvent(transaction.kind))
            this.#canvas.style.cursor = transaction.cursor;
        try {
            this.#root?.applyEventTransaction(transaction);
            this.#options.onEventTransaction?.(transaction);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "event transaction handler failed"));
        }
        this.autoFocusEditableTarget(transaction);
    }
    /** Clicking a mounted editable activates native text services engine-side. */
    autoFocusEditableTarget(transaction) {
        if (transaction.kind !== "pointerdown")
            return;
        if (this.#inputBridge.activeNodeId === transaction.target)
            return;
        // A transaction can arrive after the root is gone: the press that produced
        // it was answered by a Core running in a worker, and nothing about closing
        // recalls a message already in flight. Asking a torn-down Shell about a
        // node threw where every neighbouring handler reports.
        let state;
        try {
            if (this.#closing || this.#unmounted)
                return;
            state = this.#root?.editableState(transaction.target);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "editable state lookup failed"));
            return;
        }
        if (state === undefined)
            return;
        try {
            this.focusEditable(transaction.target);
            this.#textDragPointer = transaction.pointerId;
            this.captureTextDrag(transaction.pointerId);
            this.sendInputCommands([
                {
                    type: "placeCaret",
                    nodeId: transaction.target,
                    x: transaction.x,
                    y: transaction.y,
                    extend: (transaction.modifiers & 1) !== 0,
                    word: false,
                },
            ]);
        }
        catch (cause) {
            this.#options.onHostError?.(new Error(`editable auto-focus failed: ${String(cause)}`));
        }
    }
    /**
     * Tells the browser not to claim touch gestures Core owns.
     *
     * A non-passive listener and `preventDefault` are not enough on a touch
     * screen: the browser decides at pointerdown whether the compositor pans the
     * page, and once it has, the events are no longer cancelable. `touch-action`
     * is the only thing consulted for that decision, so a canvas that owns
     * scrolling must say so in CSS as well -- otherwise a drag scrolls the page
     * and the list never moves.
     */
    /**
     * Resizes the drawing surface to a new logical size.
     *
     * The canvas keeps a backing store in device pixels while Core lays out in
     * logical ones, so a size change has to reach both or the frame is drawn at
     * one size and stretched to another.
     */
    resize(width, height) {
        if (this.#closing || this.#unmounted)
            return;
        if (![width, height].every((value) => Number.isFinite(value) && value > 0)) {
            throw new RangeError("resize dimensions must be positive and finite");
        }
        const ratio = devicePixelRatioOf(this.#canvas);
        if (this.#client !== undefined && this.#mode !== "main-thread") {
            // The worker owns the transferred canvas, so it does the resizing.
            this.#client.postResize(width, height, ratio);
            return;
        }
        this.#frameSink?.resize(width, height, ratio);
    }
    /**
     * Follows the canvas's own box so an application does not have to.
     *
     * Every canvas-backed application faces this the moment a window is resized
     * or a phone is rotated, and a missed resize does not fail loudly: the last
     * frame is simply stretched to the new box. Observing here makes the default
     * correct; `resize` stays public for a caller that drives its own layout.
     */
    observeCanvasSize() {
        if (typeof ResizeObserver !== "function" || this.#resizeObserver !== undefined)
            return;
        this.#observedSize = `${String(this.#canvas.width)}x${String(this.#canvas.height)}`;
        this.#resizeObserver = new ResizeObserver((entries) => {
            const box = entries.at(-1)?.contentRect;
            if (box === undefined || box.width <= 0 || box.height <= 0)
                return;
            const ratio = devicePixelRatioOf(this.#canvas);
            const next = `${String(Math.round(box.width * ratio))}x${String(Math.round(box.height * ratio))}`;
            if (next === this.#observedSize)
                return;
            this.#observedSize = next;
            try {
                this.resize(box.width, box.height);
            }
            catch (cause) {
                this.#options.onHostError?.(toError(cause, "canvas resize failed"));
            }
        });
        this.#resizeObserver.observe(this.#canvas);
    }
    applyTouchAction(owned) {
        const style = this.#canvas.style;
        if (style === undefined)
            return;
        style.touchAction = owned ? "none" : "";
    }
    handleNonPassiveRegions(regions) {
        const previousPointer = this.#nonPassiveRegions.some((region) => (region.flags & 2) !== 0);
        const previousWheel = this.#nonPassiveRegions.some((region) => (region.flags & 1) !== 0);
        this.#nonPassiveRegions = regions.map((region) => Object.freeze({ ...region }));
        const nextPointer = this.#nonPassiveRegions.some((region) => (region.flags & 2) !== 0);
        const nextWheel = this.#nonPassiveRegions.some((region) => (region.flags & 1) !== 0);
        if (this.#eventListenersAttached &&
            (previousPointer !== nextPointer || previousWheel !== nextWheel)) {
            this.detachCanvasEventListeners();
            this.attachCanvasEventListeners();
        }
        this.#options.onNonPassiveRegions?.(this.#nonPassiveRegions);
    }
    handleWorkerFatal(error) {
        if (this.#closing ||
            this.#recovering ||
            this.#mode === "main-thread" ||
            this.#root === undefined)
            return;
        this.#options.onHostError?.(error);
        this.#root.resetInteractionState();
        this.#recovering = true;
        this.#recovery = this.recoverToMainThread(error).finally(() => {
            this.#recovering = false;
            this.#recovery = undefined;
        });
    }
    /**
     * Renders the newest requested window for each virtual list, once.
     *
     * A window request is an absolute range rather than a delta, so a later
     * request for the same node replaces an earlier one outright. Rendering each
     * batch separately makes the Shell walk windows the offset has already left,
     * and during a gesture those stale renders queue up faster than they drain,
     * which is what left the viewport on placeholders long after the fingers
     * stopped. Dropping a superseded window cannot lose work: Core re-requests
     * any window it still lacks after {@link REFILL_RETRY_FRAMES}.
     */
    deferVirtualRefills(requests) {
        if (requests.length === 0)
            return;
        const pending = this.#pendingRefills;
        const scheduled = pending.size > 0;
        for (const { end, nodeId, start } of requests)
            pending.set(nodeId, { end, nodeId, start });
        if (scheduled)
            return;
        // Flushed per frame, not per microtask. Core emits a window every render
        // frame, and each arrives in its own message, so a microtask flush gave
        // every one of them its own render: during a gesture the Shell rebuilt the
        // whole window nine times in sixty milliseconds, each rebuild one stride
        // behind the last, and the commits queued up so Core kept seeing windows
        // the offset had long left. Coalescing to one flush per frame renders only
        // the newest window and costs at most a frame of latency on a path that is
        // already asynchronous.
        const flush = () => {
            this.#refillFrame = undefined;
            const owned = [...pending.values()];
            pending.clear();
            if (this.#closing || this.#unmounted)
                return;
            try {
                this.#root?.refillVirtualRanges(owned);
                this.#options.onVirtualRefills?.(owned);
            }
            catch (cause) {
                this.#options.onHostError?.(toError(cause, "virtual refill handler failed"));
            }
        };
        if (typeof requestAnimationFrame === "function") {
            this.#refillFrame = requestAnimationFrame(flush);
        }
        else {
            queueMicrotask(flush);
        }
    }
    async recoverToMainThread(error) {
        const activeEditor = this.#inputBridge.activeNodeId;
        this.#inputBridge.deactivate();
        this.#recoverableSink.beginRecovery();
        this.detachCanvasEventListeners();
        this.disposeWorkerRuntime(error);
        this.#canvas = replaceTransferredCanvas(this.#canvas, this.#options);
        this.replaceInputBridge(this.#canvas);
        this.#transferred = false;
        this.#decision = runtimeFallbackDecision(this.#decision, error);
        try {
            await this.initializeMainThread(this.#canvas);
            this.attachCanvasEventListeners();
            if (activeEditor !== undefined) {
                const state = this.#root?.editableState(activeEditor);
                if (state !== undefined)
                    this.#inputBridge.activate(state);
            }
        }
        catch (cause) {
            const recoveryError = toError(cause, "main-thread recovery failed");
            this.#recoverableSink.fail(recoveryError);
            this.#options.onHostError?.(recoveryError);
        }
    }
    disposeWorkerRuntime(reason) {
        this.stopClockAnchors();
        const transport = this.#transport;
        this.#transport = undefined;
        if (transport !== undefined) {
            this.#lastTransportMetrics = transport.metrics();
            transport.abort(reason);
        }
        const client = this.#client;
        this.#client = undefined;
        this.closeInputRing();
        client?.terminate();
    }
    /**
     * Starts sampling animation-frame intervals.
     *
     * Called before the Worker handshake so the samples accumulate during WASM
     * startup instead of adding to it: by the time activation needs the value it
     * is usually there, and when it is not the field is simply omitted.
     */
    startFrameIntervalSampling() {
        const driver = this.#options.clockAnchorDriver ?? defaultClockAnchorDriver();
        if (driver === null)
            return;
        let previous;
        const frame = (timestamp) => {
            if (this.#closing)
                return;
            if (previous !== undefined)
                this.#frameIntervals.push(timestamp - previous);
            previous = timestamp;
            if (this.#frameIntervals.length >= FRAME_INTERVAL_SAMPLES)
                return;
            this.#frameIntervalHandle = driver.request(frame);
        };
        this.#frameIntervalHandle = driver.request(frame);
    }
    /** Median sampled frame interval, or undefined when it is not trustworthy. */
    measuredFrameIntervalMs() {
        if (this.#frameIntervals.length < 2)
            return undefined;
        const sorted = [...this.#frameIntervals].sort((left, right) => left - right);
        const median = sorted[Math.floor(sorted.length / 2)];
        if (median === undefined || median < MINIMUM_FRAME_MS || median > MAXIMUM_FRAME_MS) {
            return undefined;
        }
        return median;
    }
    startClockAnchors(client) {
        const driver = this.#options.clockAnchorDriver ?? defaultClockAnchorDriver();
        if (driver === null)
            return;
        let sequence = 1;
        const frame = (timestamp) => {
            if (this.#closing || client.state !== "ready")
                return;
            client.postClockAnchor(sequence, driver.timeOrigin + timestamp);
            sequence = nextSequence(sequence);
            this.#anchorHandle = driver.request(frame);
        };
        this.#anchorHandle = driver.request(frame);
    }
    startMainThreadClock(sink) {
        const driver = this.#options.clockAnchorDriver ?? defaultClockAnchorDriver();
        if (driver === null)
            return;
        this.#mainFrameTimestamp = undefined;
        const frame = (timestamp) => {
            if (this.#closing || this.#mode !== "main-thread")
                return;
            const absolute = driver.timeOrigin + timestamp;
            const previous = this.#mainFrameTimestamp;
            this.#mainFrameTimestamp = absolute;
            sink.advance(previous === undefined ? 0 : Math.max(0, absolute - previous) / 1000);
            this.#anchorHandle = driver.request(frame);
        };
        this.#anchorHandle = driver.request(frame);
    }
    stopClockAnchors() {
        const handle = this.#anchorHandle;
        this.#anchorHandle = undefined;
        this.#mainFrameTimestamp = undefined;
        const driver = this.#options.clockAnchorDriver ?? defaultClockAnchorDriver();
        if (handle !== undefined && driver !== null)
            driver.cancel(handle);
    }
    /**
     * Cancels an in-flight cadence sample.
     *
     * The sampler stops itself once it has enough, but a root closed during
     * startup would otherwise leave one request outstanding against a driver
     * that outlives it.
     */
    stopFrameIntervalSampling() {
        const handle = this.#frameIntervalHandle;
        this.#frameIntervalHandle = undefined;
        const driver = this.#options.clockAnchorDriver ?? defaultClockAnchorDriver();
        if (handle !== undefined && driver !== null)
            driver.cancel(handle);
    }
    async closeOnce() {
        this.#closing = true;
        this.stopFrameIntervalSampling();
        this.detachReducedMotionListener();
        this.detachCanvasEventListeners();
        this.stopClockAnchors();
        if (!this.#unmounted && this.#root !== undefined) {
            this.#root.unmount();
            this.#unmounted = true;
        }
        await this.#recovery;
        const transport = this.#transport;
        this.#transport = undefined;
        if (transport !== undefined) {
            await transport.close();
            this.#lastTransportMetrics = transport.metrics();
        }
        const client = this.#client;
        this.#client = undefined;
        if (client !== undefined)
            await client.close();
        this.closeInputRing();
        this.#frameSink?.dispose();
        this.#media?.close();
        this.#media = undefined;
        this.#inputBridge.dispose();
        this.#semanticMirror?.dispose();
        this.#semanticMirror = undefined;
        this.#core?.free?.();
        this.#core = undefined;
        this.#frameSink = undefined;
    }
    closeInputRing() {
        const ring = this.#inputRing;
        this.#inputRing = undefined;
        if (ring === undefined)
            return;
        this.#lastInputRingMetrics = ring.metrics();
        ring.close();
    }
    requireRoot() {
        if (this.#root === undefined)
            throw new Error("hosted root is not initialized");
        return this.#root;
    }
    requireCoreRoot() {
        if (this.#root === undefined)
            throw new Error("hosted root is not initialized");
        return this.#root;
    }
    /**
     * Scene coordinates are logical (CSS) pixels while the canvas backing store
     * is sized in device pixels, so every viewport and pointer coordinate is
     * converted here rather than at each call site.
     */
    logicalWidth() {
        return this.#canvas.width / devicePixelRatioOf(this.#canvas);
    }
    logicalHeight() {
        return this.#canvas.height / devicePixelRatioOf(this.#canvas);
    }
    createInputBridge(canvas) {
        return new NativeTextInputBridge(canvas, {
            dispatch: (command) => this.sendInputCommands([command]),
            ...(this.#options.nativeTextInputMode === "textarea-proxy" ? { editContext: null } : {}),
            onError: (error) => this.#options.onHostError?.(error),
            onSubmit: (nodeId) => this.#root?.submitEditable(nodeId),
            onCopy: () => this.#options.onDocumentCopy?.(),
            onPaste: (content) => this.#options.onDocumentPaste?.(content) === true,
            requestCharacterBounds: (nodeId, start, end) => {
                this.sendInputCommands([{ type: "requestCharacterBounds", nodeId, start, end }]);
            },
        });
    }
    /** Mirrors the committed semantic tree into the accessibility DOM. */
    /**
     * Records observed geometry, refusing anything older than what it already has.
     *
     * Under the worker transport the geometry frame and the frame report cross
     * `postMessage` independently, so ordering is not guaranteed. Accepting a
     * stale frame would move an overlay back to where it used to be; accepting
     * only non-decreasing `frameSeq` means the Shell always holds the newest
     * measurement, which is what a placement strategy wants regardless of which
     * picture is currently on screen. See docs/e8-layout-readback-design.md D9.
     */
    handleLayoutGeometry(frame) {
        if (this.#layoutGeometrySeq !== undefined && frame.frameSeq < this.#layoutGeometrySeq) {
            this.#staleLayoutGeometryFrames += 1;
            return;
        }
        this.#layoutGeometrySeq = frame.frameSeq;
        this.#layoutGeometry.clear();
        for (const record of frame.records)
            this.#layoutGeometry.set(record.nodeId, record);
        try {
            this.#root?.applyLayoutGeometry(frame.records, {
                left: 0,
                top: 0,
                width: this.#canvas.clientWidth || this.#canvas.width,
                height: this.#canvas.clientHeight || this.#canvas.height,
            });
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "layout geometry delivery failed"));
        }
        this.#options.onLayoutGeometry?.(frame);
    }
    /**
     * Latest geometry for one observed node, or undefined when it has none yet.
     *
     * Undefined is the honest answer for "not measured yet" — a zero rectangle
     * would be indistinguishable from a node that really is empty.
     */
    layoutGeometry(nodeId) {
        return this.#layoutGeometry.get(nodeId);
    }
    /**
     * The text the most recent committed frame painted, in paint order.
     *
     * Undefined until a frame arrives, or when `onPaintedText` was never
     * supplied: the probe is inert unless something asked for it.
     */
    paintedText() {
        return this.#paintedText;
    }
    /** Geometry frames dropped for arriving out of order. Diagnostic only. */
    staleLayoutGeometryFrames() {
        return this.#staleLayoutGeometryFrames;
    }
    handleSemantics(nodes) {
        try {
            this.#semanticMirror?.update(nodes);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "semantic mirror update failed"));
        }
        this.#options.onSemantics?.(nodes);
    }
    /** Feeds Core-computed editor geometry to the IME bridge automatically. */
    handleEditingGeometry(frame) {
        this.#editingGeometry = frame;
        this.flushPendingWordSelection();
        // Delivered whether or not the OS surface is over this node: a selection
        // has a place on screen before the reader has touched anything, and a
        // document's toolbar anchors to that place. The bridge check below is
        // about the surface, not the geometry.
        try {
            this.#root?.applyDocumentGeometry(frame.nodeId, frame.selectionBounds);
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "document selection geometry handler failed"));
        }
        if (this.#inputBridge.activeNodeId !== frame.nodeId)
            return;
        const toDomRect = (rect) => new DOMRect(rect.left, rect.top, rect.width, rect.height);
        const characters = frame.characterBounds;
        try {
            this.#inputBridge.updateGeometry({
                controlBounds: toDomRect(frame.controlBounds),
                selectionBounds: toDomRect(frame.selectionBounds),
                ...(characters.length === 0
                    ? {}
                    : {
                        characterBounds: (start, end) => {
                            const rects = [];
                            for (let unit = start; unit < end; unit += 1) {
                                const record = characters.find((character) => character.start <= unit && unit < character.end);
                                if (record === undefined)
                                    return rects;
                                rects.push(toDomRect(record.rect));
                            }
                            return rects;
                        },
                    }),
            });
        }
        catch (cause) {
            this.#options.onHostError?.(toError(cause, "editing geometry synchronization failed"));
        }
    }
    replaceInputBridge(canvas) {
        this.#inputBridge.dispose();
        this.#inputBridge = this.createInputBridge(canvas);
    }
}
function cursorEvent(kind) {
    return (kind === "pointerdown" ||
        kind === "pointerup" ||
        kind === "pointermove" ||
        kind === "pointercancel" ||
        kind === "pointerover" ||
        kind === "pointerout" ||
        kind === "pointerenter" ||
        kind === "pointerleave" ||
        kind === "gotpointercapture" ||
        kind === "lostpointercapture");
}
class TransportMutationSink {
    #onBackpressure;
    #transport;
    constructor(transport, onBackpressure) {
        this.#transport = transport;
        this.#onBackpressure = onBackpressure;
    }
    commit(bytes) {
        const { frameSeq } = decodeMutationBatch(bytes);
        try {
            this.#transport.enqueue(frameSeq, bytes);
        }
        catch (cause) {
            if (!(cause instanceof MutationTransportBackpressureError))
                throw cause;
            this.#onBackpressure(cause);
        }
    }
}
class RecoverableMutationSink {
    #snapshot = new MutationSceneSnapshot();
    #delegate;
    #failure;
    commit(bytes) {
        if (this.#failure !== undefined)
            throw this.#failure;
        const delegate = this.#delegate;
        this.#snapshot.applyAfterAccepted(bytes, () => delegate?.commit(bytes));
    }
    beginRecovery() {
        this.#delegate = undefined;
    }
    install(delegate) {
        if (this.#failure !== undefined)
            throw this.#failure;
        if (this.#snapshot.frameSeq !== undefined)
            delegate.commit(this.#snapshot.encode());
        this.#delegate = delegate;
    }
    fail(error) {
        this.#delegate = undefined;
        this.#failure = error;
    }
}
let sessionSequence = 1;
/** Legacy wheel-delta quantum a classic notched mouse wheel always reports. */
const WHEEL_NOTCH_LEGACY_DELTA = 120;
/** Silence after which the next wheel sample starts a new gesture. */
const WHEEL_GESTURE_GAP_MS = 200;
/** Inter-sample spacing only a continuous trackpad stream stays below. */
const WHEEL_STREAM_INTERVAL_MS = 30;
const INPUT_RING_CAPACITY = 64;
const INPUT_RING_PAYLOAD_BYTES = 4 * 1024;
function nextSessionId() {
    const result = sessionSequence;
    sessionSequence = nextSequence(sessionSequence);
    return result;
}
function nextSequence(value) {
    const next = (value + 1) >>> 0;
    return next === 0 ? 1 : next;
}
/**
 * Validates a logical canvas dimension, which is legitimately fractional.
 *
 * The logical size is the backing store divided by the device pixel ratio, and
 * a phone's ratio is routinely fractional -- 2.75 or 3.5 -- so that quotient
 * almost never lands on an integer. Requiring one rejected every such device
 * with a message that said the value was not positive when it was. Rounding
 * instead would be worse: the viewport Core lays out against would disagree
 * with the backing store by a sub-pixel and the replay scale would drift.
 */
/**
 * A canvas can transfer control to an OffscreenCanvas exactly once, and the
 * transfer outlives the root that made it. Handing the same element to a
 * second root therefore fails inside a DOM call with no hint about the cause;
 * React's StrictMode reaches this on every development mount, because it runs
 * effects twice against the element React rendered.
 */
const REUSED_CANVAS = "this canvas already transferred control to an OffscreenCanvas and cannot host a second root; " +
    "create a new canvas element per root (React StrictMode mounts effects twice)";
function transferOrExplain(canvas) {
    try {
        return canvas.transferControlToOffscreen();
    }
    catch (cause) {
        if (isInvalidState(cause))
            throw new Error(REUSED_CANVAS, { cause });
        throw cause;
    }
}
function contextOrExplain(canvas) {
    try {
        return canvas.getContext("2d", { alpha: true });
    }
    catch (cause) {
        if (isInvalidState(cause))
            throw new Error(REUSED_CANVAS, { cause });
        throw cause;
    }
}
function isInvalidState(cause) {
    return cause instanceof DOMException && cause.name === "InvalidStateError";
}
function positiveDimension(value, label) {
    if (!Number.isFinite(value) || value <= 0) {
        throw new RangeError(`${label} must be a positive finite number`);
    }
    return value;
}
function scrollNodeId(target) {
    const nodeId = typeof target === "number" ? target : target.nodeId;
    if (!Number.isInteger(nodeId) || nodeId < 0 || nodeId > 0xffff_ffff) {
        throw new RangeError("scroll target nodeId must be a u32");
    }
    return nodeId;
}
function runtimeFallbackDecision(previous, error) {
    return {
        ...previous,
        mode: "main-thread",
        reasons: [
            ...previous.reasons,
            `Worker runtime failed: ${error.message}`,
            "falling back to main-thread",
        ],
    };
}
function replaceTransferredCanvas(previous, options) {
    const replacement = previous.cloneNode(false);
    replacement.width = previous.width;
    replacement.height = previous.height;
    previous.replaceWith(replacement);
    options.onCanvasReplaced?.(replacement, previous);
    return replacement;
}
function defaultClockAnchorDriver() {
    if (typeof globalThis.requestAnimationFrame !== "function" ||
        typeof globalThis.cancelAnimationFrame !== "function") {
        return null;
    }
    return {
        cancel: (handle) => globalThis.cancelAnimationFrame(handle),
        request: (callback) => globalThis.requestAnimationFrame(callback),
        timeOrigin: performance.timeOrigin,
    };
}
/** Backing-store to CSS pixel ratio; the sink scales replay by the same value. */
function devicePixelRatioOf(_canvas) {
    const value = globalThis.devicePixelRatio;
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 1;
}
function reducedMotionPreference(value) {
    if (typeof value === "boolean")
        return value;
    const matchMedia = globalThis.matchMedia;
    if (typeof matchMedia !== "function")
        return false;
    return matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function createTransferableVideoFrame(source) {
    const constructor = globalThis.VideoFrame;
    if (constructor === undefined)
        return;
    return new constructor(source);
}
function toError(cause, message) {
    return cause instanceof Error ? cause : new Error(message, { cause });
}
//# sourceMappingURL=hosted-root.js.map